import React from 'react';
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Circle as CircleIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  Comment as CommentIcon,
  Delete as DeleteIcon,
  NotificationsActive as NotificationsActiveIcon,
  Schedule as ScheduleIcon,
  TaskAlt as TaskAltIcon,
  Warning as WarningIcon,
  Group as GroupIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

/**
 * Get icon based on notification type
 */
function getNotificationIcon(type) {
  const iconProps = { fontSize: 'small' };

  switch (type) {
    case 'TASK_ASSIGNED':
      return <AssignmentIcon {...iconProps} />;
    case 'TASK_DUE_SOON':
      return <ScheduleIcon {...iconProps} />;
    case 'TASK_OVERDUE':
      return <WarningIcon {...iconProps} />;
    case 'TASK_COMPLETED':
      return <TaskAltIcon {...iconProps} />;
    case 'STAGE_CHANGED':
      return <TrendingUpIcon {...iconProps} />;
    case 'COMPANY_ASSIGNED':
    case 'COMPANY_CREATED':
      return <BusinessIcon {...iconProps} />;
    case 'DOCUMENT_UPLOADED':
      return <DescriptionIcon {...iconProps} />;
    case 'MEETING_SCHEDULED':
    case 'MEETING_REMINDER':
      return <EventIcon {...iconProps} />;
    case 'COMMENT_MENTION':
      return <CommentIcon {...iconProps} />;
    case 'TEAM_MEMBER_ADDED':
      return <GroupIcon {...iconProps} />;
    case 'EMAIL_SENT':
      return <EmailIcon {...iconProps} />;
    case 'GENERAL':
      return <NotificationsActiveIcon {...iconProps} />;
    default:
      return <CircleIcon {...iconProps} />;
  }
}

/**
 * Get color based on notification type
 */
function getNotificationColor(type) {
  switch (type) {
    case 'TASK_ASSIGNED':
      return '#1976d2'; // blue
    case 'TASK_DUE_SOON':
      return '#ff9800'; // orange
    case 'TASK_OVERDUE':
      return '#f44336'; // red
    case 'TASK_COMPLETED':
      return '#4caf50'; // green
    case 'STAGE_CHANGED':
      return '#2e7d32'; // dark green
    case 'COMPANY_ASSIGNED':
    case 'COMPANY_CREATED':
      return '#ed6c02'; // amber
    case 'DOCUMENT_UPLOADED':
      return '#9c27b0'; // purple
    case 'MEETING_SCHEDULED':
    case 'MEETING_REMINDER':
      return '#d32f2f'; // red
    case 'COMMENT_MENTION':
      return '#0288d1'; // light blue
    case 'TEAM_MEMBER_ADDED':
      return '#673ab7'; // deep purple
    case 'EMAIL_SENT':
      return '#00796b'; // teal
    default:
      return '#757575'; // gray
  }
}

/**
 * NotificationItem Component
 * Displays a single notification in the list
 */
function NotificationItem({ notification, onClick, onMarkAsRead, onDelete, showActions = false }) {
  const { id, type, title, body, is_read, created_at } = notification;

  const icon = getNotificationIcon(type);
  const color = getNotificationColor(type);

  const timeAgo = created_at
    ? formatDistanceToNow(new Date(created_at), { addSuffix: true })
    : '';

  return (
    <ListItem
      button
      onClick={onClick}
      sx={{
        bgcolor: is_read ? 'transparent' : 'action.hover',
        '&:hover': {
          bgcolor: is_read ? 'action.hover' : 'action.selected',
        },
        py: 1.5,
        px: 2,
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
    >
      {/* Icon/Avatar */}
      <ListItemAvatar>
        <Avatar
          sx={{
            bgcolor: color,
            width: 40,
            height: 40
          }}
        >
          {icon}
        </Avatar>
      </ListItemAvatar>

      {/* Content */}
      <ListItemText
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{ component: 'div' }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: is_read ? 400 : 600,
                flex: 1,
                color: 'text.primary'
              }}
            >
              {title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {!is_read && (
                <Tooltip title="Mark as read">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAsRead();
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
              {showActions && onDelete && (
                <Tooltip title="Delete notification">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    sx={{ p: 0.5 }}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {body}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {timeAgo}
            </Typography>
          </Box>
        }
      />

      {/* Unread indicator dot */}
      {!is_read && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            ml: 1,
            flexShrink: 0
          }}
        />
      )}
    </ListItem>
  );
}

export default NotificationItem;
